// brand logo preloading (warmLogos): lowercase, accents off, letters/digits only (Arabic kept)
let bjoLogoMap;
const bjoWarmed = new Set();
const bjoKey = (s) => s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // before typing: tap a recent search, × removes one, Clear history empties the list (Hamza 2026-10-25)
    this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this));
    // keep the keyboard + panel open while tapping × / Clear (a tap on a button would blur the input and close the panel)
    // Enter saves what the customer typed: on window, before bjo-search-aliases.js swaps "lego" for "LEGO®" on document
    window.addEventListener('submit', (e) => { if (e.target === this.input.form) this.rsSave(this.getQuery()); }, true);
    // and show it as soon as the search sheet opens (doesn't wait for the focus event, which can come late in the app)
    this.closest('details')?.addEventListener('toggle', (e) => { if (e.target.open && !this.getQuery()) this.showRecent(); });
    this.predictiveSearchResults.addEventListener('pointerdown', (e) => { if (e.target.closest('.bjo-rs__x, .bjo-rs__clear')) e.preventDefault(); });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.removeAttribute('results');
      this.showRecent();
      return;
    }
    this.querySelector('.bjo-rs')?.remove(); // typing: the recent panel makes way for the live results

    this.warmLogos(this.searchTerm);
    this.getSearchResults(this.searchTerm);
  }

  // Every brand logo (templates/collection.bjo-logos.liquid), loaded once when search is tapped and kept a day on this phone.
  // While the search request runs, the logos of brands matching the typed text start loading too, so a brand pill
  // shows with its logo instead of filling in a moment later (Hamza 2026-11-02: "the brand's logo takes time to load").
  loadLogoMap() {
    if (bjoLogoMap !== undefined) return;
    bjoLogoMap = null;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/', key = 'bjoLogoMap:' + root;
    const use = (d) => { bjoLogoMap = d.map(([h, t, u]) => [bjoKey(h.replace(/-/g, ' ') + ' ' + t).split(' '), bjoKey(t).replace(/ /g, ''), u]); };
    try { const c = JSON.parse(localStorage.getItem(key)); if (c && Date.now() - c.t < 864e5) return use(c.d); } catch (e) {}
    fetch(root + 'collections/all?view=bjo-logos').then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!Array.isArray(d)) return;
      use(d);
      try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), d })); } catch (e) {}
      if (this.searchTerm) this.warmLogos(this.searchTerm);
    }).catch(() => {});
  }

  warmLogos(term) {
    const q = bjoKey(term).replace(/ /g, '');
    if (!bjoLogoMap || q.length < 2) return;
    let n = 0;
    for (const [words, joined, u] of bjoLogoMap) {
      if (!joined.startsWith(q) && !words.some((w) => w.startsWith(q))) continue;
      if (!bjoWarmed.has(u)) { bjoWarmed.add(u); new Image().src = u; }
      if (++n >= 6) break;
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.showRecent();
    }
  }

  onFocus() {
    // warm the browser cache with the top brand logos as soon as search is tapped (snippets/bjo-logo-warm.liquid)
    if (window.bjoLogoWarm && !window.bjoLogoWarmed) { window.bjoLogoWarmed = 1; window.bjoLogoWarm.forEach((u) => { if (!bjoWarmed.has(u)) { bjoWarmed.add(u); new Image().src = u; } }); }
    this.loadLogoMap();
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.showRecent();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const active = document.activeElement;
      if (active && (this.contains(active) || active.closest?.('predictive-search'))) return;
      this.close();
    }, 100);
  }

  onKeyup(event) {
    if (!this.getQuery().length && !this.querySelector('.bjo-rs')) this.showRecent();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText) return;

    const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
    if (matches && matches.length > 1) return;

    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null && !element.classList.contains('bjo-ps-row')
    );

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    for (let i = 0; i < allVisibleElements.length; i++) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
        break;
      }
    }

    if (this.statusElement) this.statusElement.textContent = ''; // not set yet when arrowing through the recent panel before any search

    let activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Arabic/odd brand spellings people type (ليجو -> lego): assets/bjo-search-aliases.js
    if (window.bjoAlias) searchTerm = window.bjoAlias(searchTerm);
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();
    // cancel the previous letter's search, so a slow "ba" answer can't land on top of "baby" (Hamza 2026-10-15)
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const isArabic = (window.location.pathname || '/').toLowerCase().startsWith('/ar');

    // ✅ Arabic: use JSON suggest (works even though Arabic isn't supported by Shopify predictive)
    if (isArabic) {
      // Shopify predictive search has no Arabic: products/pages come from the normal Arabic search
      // (templates/search.bjo-suggest.liquid); collections from the English index, retitled in Arabic.
      const q = encodeURIComponent(searchTerm);
      const signal = this.abortController.signal;
      const getJson = (u) => fetch(u, { signal }).then((r) => (r.ok ? r.json() : null)).catch((e) => { if (e?.name === 'AbortError') throw e; return null; });
      const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c]));
      // collections: English index + Arabic titles (assets/bjo-ar-collections.json, rebuilt by ~/Claude/Projects/search-pills/ar_cols) so "مناديل" finds "حفاضات ومناديل مبللة"
      const arNorm = (t) => String(t || '').normalize('NFKC').toLowerCase().replace(/[ً-ْـ]/g, '').replace(/[أإآ]/g, 'ا').replace(/ى/g, 'ي').replace(/ة/g, 'ه');
      const arStem = (w) => w.replace(/^(وال|بال|لل|ال|و)(?=..)/, '');
      const AR_SYN = { 'وايبس': 'مناديل', 'وايبز': 'مناديل', 'وايب': 'مناديل' };
      const arWords = arNorm(searchTerm).split(/\s+/).filter(Boolean).map((w) => arStem(AR_SYN[w] || w));
      const arIndex = window.bjoArCols && arWords.length && /[\u0600-\u06FF]/.test(searchTerm)
        ? (window.bjoArColsData ? Promise.resolve(window.bjoArColsData) : getJson(window.bjoArCols).then((d) => (window.bjoArColsData = d || [])))
            .then((rows) => rows.filter(([, t]) => { const ws = arNorm(t).split(/\s+/).map(arStem); return arWords.every((a) => ws.some((w) => w.startsWith(a))); }).map(([h]) => h))
        : Promise.resolve([]);
      // Same-Day page + Sale collection right after brands when the search starts like them, EN or AR words (same as the English section)
      const kt = arNorm(searchTerm).trim();
      const kHit = (ws) => kt.length > 1 && ws.some((w) => arNorm(w).startsWith(kt));
      const keySD = kHit(['sameday', 'same day', 'same-day', 'delivery', 'توصيل', 'نفس اليوم', 'سيم داي']);
      const keySale = kHit(['sale', 'sales', 'offers', 'discount', 'deals', 'عروض', 'تخفيضات', 'خصم', 'خصومات', 'تنزيلات', 'سيل']);
      const saleP = keySale ? getJson('/ar/collections/on-sale-offers?view=bjo-title') : Promise.resolve(null);
      const collectionsP = Promise.all([getJson(`/search/suggest.json?q=${q}&resources[type]=collection&resources[limit]=10`), arIndex]).then(([d, arHandles]) => {
        // same hidden list as sections/predictive-search.liquid
        const HIDE = ['adults','age-0-12-months','age-1-2-years','age-3-6-years','age-6-years-plus','backpacks','biggest-savings','sale-50-plus','all-sale-items','toys-on-sale','size-1-bibs-pacifier','size-2-bibs-pacifier','size-3-bibs-pacifier','bibs-bottles','mustela-packages','mustela-hydration','mustela-cleansing-bath','mustela-sun-care','mustela-diaper-change','bath-toys','bath-safety','bath-1','bath-books','peekaboo-supplier','bambini-imports','little-kittens-supplier'];
        // our own imports first when the search starts like their name ("b" → Baby Brezza, BIBS, BabyBjörn), same list + order as sections/predictive-search.liquid
        const PRI = (/* BJO-RANK */ 'baby-brezza:baby brezza,bibs:bibs,babybjorn:babybjorn,beaba:beaba,boon:boon,mushie:mushie,love-to-dream:love to dream,sunnylife:sunnylife,gebe-maternity:gebe maternity,the-nursery-co:the nursery & co,jack-n-jill:jack n jill,lal:lal,momcozy:momcozy,dyson:dyson,waterwipes:waterwipes,ninja:ninja,skip-hop:skip hop,spectra:spectra,delonghi:delonghi,tommee-tippee:tommee tippee,bambo-nature:bambo nature,dr-browns:dr. browns,mustela:mustela,joie:joie,attitude:attitude,chicco:chicco,smeg:smeg,tineco:tineco,stephen-joseph:stephen joseph,nutricook:nutricook,frida-baby:frida baby,melissa-doug:melissa & doug,hape:hape,step2:step2,infantino:infantino,babyjem:babyjem,biolane:biolane,lego®:lego,bbox:bbox,revlon:revlon,dolu:dolu,dreame:dreame,nuna:nuna,mideer:mideer,hydro-flask:hydro flask,instant-pot:instant pot,shark:shark,little-tikes:little tikes,farlin:farlin,babymoov:babymoov,intex:intex,winfun:winfun,nattou:nattou,aiwibi:aiwibi,ecovacs-robotics:ecovacs robotics,slipstop:slipstop,braun:braun,jansport:jansport,evenflo:evenflo,philips-avent:philips avent,bebe-confort:bebe confort,omron:omron,yumbox:yumbox,bodra:bodra,fisher-price:fisher price,medela:medela,oral-b:oral-b,apple:apple,havaianas:havaianas,omielife:omielife,karcher-1:karcher,the-coldest-water:the coldest water,yope:yope,stanley:stanley,frida-mom:frida mom,xiaomi:xiaomi,play-doh:play-doh,little-believer:little believer,adam-mishmish:adam wa mishmish,nutribullet:nutribullet,beurer:beurer,vtech:vtech,kiddylicious:kiddylicious,abena:abena,viga:viga,cecotec:cecotec,bobs-red-mill:bobs red mill,aer:aer,micro-scooters:micro scooters,eastpak:eastpak,squishmallows:squishmallows,graco:graco,septona:septona,kalila-wa-dimna:kalila wa dimna,contigo:contigo,razor:razor,globber:globber,kipling:kipling,lansinoh:lansinoh,vicks:vicks,munchkin:munchkin,usborne:usborne,roofa:roofa,ababy:ababy,yippee:yippee,panasonic:panasonic,diaperchamp:diaperchamp,barbie:barbie,maxi-cosi:maxi-cosi,hola:hola,huffy-bicycles:huffy bicycles,huggies:huggies,babyccino:babyccino,zoggs:zoggs,thermos:thermos,safety-1st:safety 1st,pureborn:pureborn,bioderma:bioderma,klutz:klutz,yvolution:yvolution,aminas-natural-skin-care:aminas natural skin care,herbatint:herbatint,levoit:levoit,boldcube:bold cube,carl-oscar:carl oscar,hamilton-beach:hamilton beach,miniland:miniland,47-brand:47 brand,cybex:cybex,pzl:pzl,primo:primo,wigiwama:wigiwama,disney:disney,varta:varta,joseph-joseph:joseph joseph,casio:casio,the-source:the source,micador:micador,fresk:fresk,sistema:sistema,marcus-marcus:marcus & marcus,clix-tiles:clix-tiles,remington:remington,nova-home:nova home,leapfrog:leapfrog,greenjo:greenjo,hot-wheels:hot wheels,x-shot:x-shot,clementoni:clementoni,penguin:penguin,cocomelon:cocomelon,mama-yoyo:mama yoyo').split(',').filter(Boolean).map((e) => e.split(':')); // own imports, then brands by sales: ~/Claude/Projects/search-pills/build_rank.py
        const priHandles = kt ? PRI.filter(([, n]) => n.startsWith(kt) || (kt.length >= 4 && n.includes(kt))).map(([h]) => h).slice(0, 10) : [];
        const handles = [...new Set([...priHandles, ...(d?.resources?.results?.collections || []).map((c) => c.handle), ...arHandles])].filter((h) => !HIDE.includes(h) && !(keySale && h === 'on-sale-offers') && !h.includes('-back-to-school') && !h.includes('supplier') && !h.includes('same-day') && !h.includes('sameday')).slice(0, 10 + priHandles.length);
        return Promise.all(handles.map((h) => getJson(`/ar/collections/${h}?view=bjo-title`)));
      });
      Promise.all([getJson(`/ar/search?q=${q}&type=product,page,article&options[prefix]=last&view=bjo-suggest`), collectionsP, saleP])
        .then(([data, cols, saleCol]) => {
          const products = data?.products || [];
          const isSDp = (x) => /\/pages\/same-?day/.test(String(x.url || ''));
          const sdOn = keySD || (data?.pages || []).some(isSDp); // Same-Day page goes right after the brands whenever it is found
          const pages = (data?.pages || []).filter((x) => !isSDp(x));
          const collections = (cols || []).filter(Boolean);
          // same markup + ids as the English section, so the grey chips / product rows styling applies in Arabic too
          const isSD = (i) => /\/pages\/(same-?day)/.test(String(i.url || ''));
          const textItem = (i) => `
            <li class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${i.url}" class="predictive-search__item link link--text${isSD(i) ? ' bjo-ps-sameday' : ''}" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">${isSD(i) ? `${window.bjoSdTag ? `<img class="bjo-ps-sd-img" src="${window.bjoSdTag}" alt="SAMEDAY" width="596" height="129">` : ''}<span class="bjo-ps-sd-word">توصيل</span>` : esc(String(i.title || '').split(' | ')[0].trim())}</p>
                </div>
              </a>
            </li>`;
          const collectionItem = (c) => c.logo ? `
            <li class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${c.url}" class="predictive-search__item link link--text bjo-ps-logo" tabindex="-1" aria-label="${esc(String(c.title || '').split(' | ')[0].trim())}">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <img class="bjo-ps-logo__img${c.lw && c.lh && c.lw / c.lh < 2.2 ? ' bjo-ps-logo__img--sq' : ''}" src="${c.logo}" alt="${esc(String(c.title || '').split(' | ')[0].trim())}" width="${c.lw || ''}" height="${c.lh || ''}" loading="lazy">
                  <p class="predictive-search__item-heading h5">${esc(String(c.title || '').split(' | ')[0].trim())}</p>
                </div>
              </a>
            </li>` : textItem(c);
          const productItem = (p) => `
            <li class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${p.url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                ${p.image ? `<img class="predictive-search__image" src="${p.image}" alt="${esc(p.alt)}" width="50" loading="lazy">` : ''}
                <div class="predictive-search__item-content">
                  <div class="predictive-search__item-vendor caption-with-letter-spacing">${esc(p.vendor)}</div>
                  <p class="predictive-search__item-heading h5">${esc(p.title)}</p>
                  ${p.price_html || ''}
                </div>
              </a>
            </li>`;
          const list = (id, items) => `<ul id="${id}" class="predictive-search__results-list list-unstyled" role="group">${items}</ul>`;
          // pages + blog posts sit at the bottom, under the products (Hamza 2026-10-15); blog = page icon, short label like English
          const BLOG_IC = '<span class="bjo-ps-ic" aria-hidden="true"><svg viewBox="8 8 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="12.6" y="10.4" width="14.8" height="19.2" rx="3"/><path d="M16.2 16h7.6M16.2 20h7.6M16.2 24h4.6"/></svg></span>';
          const shortT = (t) => { const w = String(t || '').split(':')[0].split(' | ')[0].split(' – ')[0].split(' - ')[0].trim().split(/\s+/); return w.length > 5 ? w.slice(0, 5).join(' ') + '…' : w.join(' '); };
          const blogItem = (a) => `
            <li class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${a.url}" class="predictive-search__item link link--text bjo-ps-chip" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">${BLOG_IC}<p class="predictive-search__item-heading h5" title="${esc(a.title)}">${esc(shortT(a.title))}</p></div>
              </a>
            </li>`;
          const pageList = pages.filter((x) => x.type !== 'article');
          const blogList = pages.filter((x) => x.type === 'article');
          const bottom = [
            pageList.length ? `<div class="predictive-search__pages-wrapper bjo-ps-articles"><h2 class="predictive-search__heading text-body caption-with-letter-spacing">الصفحات</h2>${list('predictive-search-results-pages-list-ar-pages', pageList.map(textItem).join(''))}</div>` : '',
            blogList.length ? `<div class="predictive-search__pages-wrapper bjo-ps-articles"><h2 class="predictive-search__heading text-body caption-with-letter-spacing">من مدونتنا</h2>${list('predictive-search-results-pages-list-mobile', blogList.map(blogItem).join(''))}</div>` : '',
          ].join('');
          const side = [
            (collections.length || sdOn || saleCol) ? `<div><h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">اقتراحات</h2>${list('predictive-search-results-queries-list', [collections.some((c) => c.brand) ? `<li class="bjo-ps-row bjo-ps-row--brands" role="presentation"><ul class="bjo-ps-rowlist list-unstyled">${collections.filter((c) => c.brand).map(collectionItem).join('')}</ul></li>` : '', `<li class="bjo-ps-row" role="presentation"><ul class="bjo-ps-rowlist list-unstyled">${[...(sdOn ? [{ url: '/ar/pages/sameday-delivery' }] : []), ...(saleCol ? [saleCol] : []), ...collections.filter((c) => !c.brand)].map(collectionItem).join('')}</ul></li>`].join(''))}</div>` : '',

          ].join('');
          const resultsMarkup = `
            <div id="predictive-search-results" role="listbox">
            <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
              ${side ? `<div class="predictive-search__result-group">${side}</div>` : ''}
              <div class="predictive-search__result-group">
                ${products.length ? `<div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">المنتجات</h2>${list('predictive-search-results-products-list', products.map(productItem).join(''))}</div>` : `<p style="padding:1rem 2rem;">لا توجد منتجات</p>`}
                ${bottom}
              </div>
            </div>
            </div>`;

          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
          searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        })
        .catch((error) => {
          searchDeferred?.reject(error);
          if (error?.name !== 'AbortError') this.close();
        });

      return;
    }

    // ✅ English / supported locales: original Dawn behavior
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=7&resources[limit_scope]=each`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  // ---- Before typing (Hamza 2026-10-25): last 5 searches + recently viewed. Saved on this device only, one list per
  // signed-in customer (data-cid in snippets/header-search.liquid) and one for guests, so a shared phone never shows
  // another account's searches. Products come from bjoRecent/bjoRecentInfo (assets/bjo-recently-viewed.js): no request
  // unless an old history entry has no saved details yet.
  rsKey() {
    return 'bjoSearch:' + (this.dataset.cid || 'guest');
  }

  // an entry is { t: words } for a typed search, or { t: name, u: link, i: logo } for a brand/collection picked from the
  // suggestions ("ba" → tap Baby Brezza saves Baby Brezza with its logo, not "ba". Hamza 2026-10-25)
  rsList() {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(this.rsKey()) || '[]'); } catch (e) {}
    return list.map((e) => (typeof e === 'string' ? { t: e } : e)).filter((e) => e && e.t);
  }

  rsWrite(list) {
    try { list.length ? localStorage.setItem(this.rsKey(), JSON.stringify(list)) : localStorage.removeItem(this.rsKey()); } catch (e) {}
  }

  rsSave(entry) {
    if (typeof entry === 'string') entry = { t: entry };
    entry.t = String(entry.t || '').trim().slice(0, 60);
    if (entry.t) this.rsWrite([entry, ...this.rsList().filter((e) => e.t.toLowerCase() !== entry.t.toLowerCase())].slice(0, 5));
  }

  onResultsClick(event) {
    const x = event.target.closest('.bjo-rs__x');
    const clear = event.target.closest('.bjo-rs__clear');
    if (x || clear) {
      event.preventDefault();
      event.stopPropagation(); // the button is gone after the redraw: the sheet's click-outside check would close it
      this.rsWrite(clear ? [] : this.rsList().filter((e) => e.t !== x.dataset.term));
      this.showRecent();
      return;
    }
    const pill = event.target.closest('.bjo-rs__pill a');
    if (pill) return this.rsSave(this.rsList().find((e) => e.t === pill.dataset.term) || pill.dataset.term); // moves it to the front
    const a = event.target.closest('a');
    if (!a) return;
    if (a.classList.contains('predictive-search__item--link-with-thumbnail')) return; // a product: it shows under Recently viewed
    if (a.classList.contains('predictive-search__item')) {
      // a brand / category / page pill: save it as itself
      const img = a.querySelector('.bjo-ps-logo__img');
      const name = a.getAttribute('aria-label') || a.textContent.trim() || (a.querySelector('img') || {}).alt;
      const typed = this.getQuery().toLowerCase(); // the "ba" typed on the way there doesn't stay in the list
      if (typed) this.rsWrite(this.rsList().filter((e) => e.u || e.t.toLowerCase() !== typed));
      return this.rsSave({ t: name, u: a.pathname, i: img ? img.currentSrc || img.src : '' });
    }
    if (this.getQuery()) this.rsSave(this.getQuery());
  }

  rsViewed() {
    const lang = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    let handles = [], info = {};
    try {
      handles = JSON.parse(localStorage.getItem('bjoRecent') || '[]');
      info = JSON.parse(localStorage.getItem('bjoRecentInfo') || '{}');
    } catch (e) {}
    const here = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    handles = handles.filter((h) => h !== here).slice(0, 6);
    const missing = handles.filter((h) => !info[h] || !info[h].t || !info[h].t[lang]);
    if (missing.length && !this.rsFilling) this.rsFill(missing, lang);
    return handles.filter((h) => info[h] && info[h].t && (info[h].t[lang] || info[h].t.en || info[h].t.ar)).map((h) => ({
      h, v: info[h].v || '', i: info[h].i || '', t: info[h].t[lang] || info[h].t.en || info[h].t.ar,
    }));
  }

  // old history (saved before 2026-10-25) has only handles: fetch those products once, keep their details, redraw
  rsFill(handles, lang) {
    this.rsFilling = true;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    Promise.all(handles.map((h) => fetch(root + 'products/' + h + '.js').then((r) => (r.ok ? r.json() : null)).catch(() => null)))
      .then((list) => {
        let info = {};
        try { info = JSON.parse(localStorage.getItem('bjoRecentInfo') || '{}'); } catch (e) {}
        list.forEach((p, k) => {
          const h = handles[k], cur = info[h] || { t: {} };
          if (!p) { cur.gone = 1; info[h] = cur; return; }
          cur.t = Object.assign({}, cur.t, { [lang]: p.title });
          cur.v = p.vendor;
          cur.i = p.featured_image ? p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=150' : '';
          info[h] = cur;
        });
        try { localStorage.setItem('bjoRecentInfo', JSON.stringify(info)); } catch (e) {}
        if (!this.getQuery() && this.querySelector('.bjo-rs')) this.showRecent();
      });
  }

  showRecent() {
    const ar = document.documentElement.lang === 'ar';
    const esc = (t) => String(t || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const searches = this.rsList();
    const viewed = this.rsViewed();
    if (!searches.length && !viewed.length) {
      this.querySelector('.bjo-rs')?.remove();
      this.close();
      return;
    }
    const action = this.input.form.getAttribute('action') || '/search';
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const T = ar
      ? { recent: 'آخر عمليات البحث', clear: 'مسح السجل', viewed: 'شاهدتها مؤخرًا', remove: 'حذف' }
      : { recent: 'Recent searches', clear: 'Clear history', viewed: 'Recently viewed', remove: 'Remove' };
    // past search = clock (BambiniJO thin-line icon, ~/Claude/Brand/icons/clock.svg, Hamza 2026-10-25)
    const CLOCK = '<svg class="bjo-rs__clock" viewBox="8 8 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="20" cy="20" r="9.6"/><path d="M20 14.6V20l3.6 2.2"/></svg>';
    const pills = searches.map((e, k) => `
      <li class="bjo-rs__pill${e.i ? ' bjo-rs__pill--logo' : ''}" id="bjo-rs-s${k}" role="option" aria-selected="false">
        <a href="${e.u ? esc(e.u) : `${action}?q=${encodeURIComponent(e.t)}&options%5Bprefix%5D=last`}" data-term="${esc(e.t)}" tabindex="-1"${e.i ? ` aria-label="${esc(e.t)}"` : ''}>${e.i ? `<img src="${esc(e.i)}" alt="${esc(e.t)}" height="20" loading="lazy">` : `${CLOCK}<span>${esc(e.t)}</span>`}</a>
        <button type="button" class="bjo-rs__x" data-term="${esc(e.t)}" aria-label="${T.remove} ${esc(e.t)}" tabindex="-1"><svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>
      </li>`).join('');
    const rows = viewed.map((p, k) => `
      <li class="predictive-search__list-item" id="bjo-rs-p${k}" role="option" aria-selected="false">
        <a href="${root}products/${p.h}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
          ${p.i ? `<img class="predictive-search__image" src="${esc(p.i)}" alt="" width="50" loading="lazy">` : ''}
          <div class="predictive-search__item-content">
            ${p.v ? `<div class="predictive-search__item-vendor caption-with-letter-spacing">${esc(p.v)}</div>` : ''}
            <p class="predictive-search__item-heading h5">${esc(p.t)}</p>
          </div>
        </a>
      </li>`).join('');
    this.predictiveSearchResults.innerHTML = `<div class="bjo-rs">
      ${searches.length ? `<div class="bjo-rs__head"><h2 class="predictive-search__heading text-body caption-with-letter-spacing">${T.recent}</h2><button type="button" class="bjo-rs__clear" tabindex="-1">${T.clear}</button></div>
      <ul class="bjo-rs__pills list-unstyled" role="group">${pills}</ul>` : ''}
      ${viewed.length ? `<h2 class="predictive-search__heading text-body caption-with-letter-spacing">${T.viewed}</h2>
      <ul class="predictive-search__results-list list-unstyled" role="group">${rows}</ul>` : ''}
    </div>`;
    // desktop: the panel lines up exactly under the search field (the results box itself spans the whole page)
    const rs = this.querySelector('.bjo-rs'), field = this.querySelector('.field');
    if (field && window.matchMedia('(min-width: 750px)').matches) {
      const f = field.getBoundingClientRect(), b = this.predictiveSearchResults.getBoundingClientRect();
      Object.assign(rs.style, { width: f.width + 'px', marginLeft: f.left - b.left + 'px', paddingInline: '0' });
    }
    this.open();
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    // drop a suggestions row with nothing in it, so no stray line shows (brands-only searches like "nin")
    this.querySelectorAll('li.bjo-ps-row').forEach((r) => { if (!r.querySelector('li[role="option"]')) r.remove(); });
    // phones: each suggestion row = max 2 lines that swipe together; items fill line 1 to about half the total width, the rest go
    // on line 2 (same 6px gaps, lines start-aligned, one may be longer). A row wider than the screen fades at its far edge.
    requestAnimationFrame(() => this.querySelectorAll('.bjo-ps-rowlist').forEach((r) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const items = [...r.children].filter((c) => c.tagName === 'LI');
      const widths = items.map((li) => li.offsetWidth + 6); // measure before moving (detached items measure 0)
      const total = widths.reduce((a, b) => a + b, 0);
      if (total - 6 <= r.clientWidth) return; // fits on one line (no gap after the last pill)
      // only a little too wide (e.g. 2 pills): keep ONE line that swipes, no second line (Hamza 2026-10-16)
      if (items.length <= 2 || total < r.clientWidth * 1.4) {
        const l = document.createElement('div'); l.className = 'bjo-ps-line';
        items.forEach((li) => l.appendChild(li)); r.append(l);
        r.classList.add('is-lines');
        r.classList.toggle('is-overflow', r.scrollWidth > r.clientWidth + 2); // fade only when there really is more to swipe to (Hamza 2026-10-24)
        return;
      }
      const l1 = document.createElement('div'), l2 = document.createElement('div');
      l1.className = l2.className = 'bjo-ps-line';
      // split where the two lines come out closest in length (order kept)
      let cut = 1, best = Infinity, w = 0;
      widths.forEach((x, k) => { w += x; const m = Math.max(w, total - w); if (m < best) { best = m; cut = k + 1; } });
      items.forEach((li, k) => (k < cut ? l1 : l2).appendChild(li));
      r.append(l1, l2);
      r.classList.add('is-lines');
      r.classList.toggle('is-overflow', r.scrollWidth > r.clientWidth + 2);
    }));

    this.setLiveRegionResults();
    this.open();
  }

  // ✅ prevent crash in Arabic (no live-region count element)
  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(countEl?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);